/**
 * Color utility functions for white-label branding.
 * All functions are pure — no side effects, no DOM access.
 */

/** Converts a hex color string to space-separated HSL values (e.g. "215 82% 50%"). */
export function hexToHSL(hex: string): string {
  const { h, s, l } = hexToHSLValues(hex);
  return `${h} ${s}% ${l}%`;
}

/** Returns numeric H, S, L components from a hex color string. */
export function hexToHSLValues(hex: string): { h: number; s: number; l: number } {
  let r: number, g: number, b: number;
  const cleaned = hex.replace('#', '');

  if (cleaned.length === 3) {
    r = parseInt(cleaned[0] + cleaned[0], 16) / 255;
    g = parseInt(cleaned[1] + cleaned[1], 16) / 255;
    b = parseInt(cleaned[2] + cleaned[2], 16) / 255;
  } else {
    r = parseInt(cleaned.substring(0, 2), 16) / 255;
    g = parseInt(cleaned.substring(2, 4), 16) / 255;
    b = parseInt(cleaned.substring(4, 6), 16) / 255;
  }

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/** Converts HSL values back to a hex string. */
export function hslToHex(h: number, s: number, l: number): string {
  const sNorm = s / 100;
  const lNorm = l / 100;

  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lNorm - c / 2;

  let r = 0, g = 0, b = 0;

  if (h < 60) { r = c; g = x; b = 0; }
  else if (h < 120) { r = x; g = c; b = 0; }
  else if (h < 180) { r = 0; g = c; b = x; }
  else if (h < 240) { r = 0; g = x; b = c; }
  else if (h < 300) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }

  const toHex = (v: number) => {
    const hex = Math.round((v + m) * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

/**
 * Generates a 10-color chart palette from two brand colors.
 * Colors 1-2: primary and accent.
 * Colors 3-5: interpolated between primary and accent with lightness adjustments.
 * Colors 6-10: hue-shifted from primary in 30-degree increments.
 */
export function generateChartPalette(primaryHex: string, accentHex: string): string[] {
  const p = hexToHSLValues(primaryHex);
  const a = hexToHSLValues(accentHex);

  const colors: string[] = [
    `hsl(${p.h}, ${p.s}%, ${p.l}%)`,
    `hsl(${a.h}, ${a.s}%, ${a.l}%)`,
  ];

  // Colors 3-5: interpolate between primary and accent
  const midH = p.h + (((a.h - p.h + 540) % 360) - 180) * 0.5;
  const midS = Math.round((p.s + a.s) / 2);
  const midL = Math.round((p.l + a.l) / 2);

  colors.push(`hsl(${Math.round(((midH % 360) + 360) % 360)}, ${midS}%, ${Math.min(midL + 12, 85)}%)`);
  colors.push(`hsl(${Math.round(((midH % 360) + 360) % 360)}, ${Math.max(midS - 10, 20)}%, ${Math.min(midL + 24, 85)}%)`);
  colors.push(`hsl(${Math.round(((midH % 360) + 360) % 360)}, ${midS}%, ${Math.max(midL - 10, 15)}%)`);

  // Colors 6-10: hue-shifted from primary
  for (let i = 1; i <= 5; i++) {
    const h = (p.h + i * 30) % 360;
    colors.push(`hsl(${h}, ${Math.max(p.s - 10, 20)}%, ${Math.min(p.l + 5, 70)}%)`);
  }

  return colors;
}

/** Adjusts HSL values for dark mode by increasing lightness by 5%. */
export function adjustForDarkMode(h: number, s: number, l: number): { h: number; s: number; l: number } {
  return { h, s, l: Math.min(l + 5, 100) };
}

/**
 * Calculates WCAG 2.1 contrast ratio between two hex colors.
 * Returns a number between 1 (identical) and 21 (black on white).
 */
export function contrastRatio(hex1: string, hex2: string): number {
  const luminance = (hex: string): number => {
    const cleaned = hex.replace('#', '');
    let r: number, g: number, b: number;

    if (cleaned.length === 3) {
      r = parseInt(cleaned[0] + cleaned[0], 16) / 255;
      g = parseInt(cleaned[1] + cleaned[1], 16) / 255;
      b = parseInt(cleaned[2] + cleaned[2], 16) / 255;
    } else {
      r = parseInt(cleaned.substring(0, 2), 16) / 255;
      g = parseInt(cleaned.substring(2, 4), 16) / 255;
      b = parseInt(cleaned.substring(4, 6), 16) / 255;
    }

    const linearize = (c: number) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
  };

  const l1 = luminance(hex1);
  const l2 = luminance(hex2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}
